import {
  MAILBOX_PROVIDERS,
  type HostSpec,
  type MailboxProvider,
  type MailboxProviderId,
  emailDomainOf,
  mailboxHostsOf,
  mailboxProviderOf,
} from 'fym-shared/src/music24/outreach/mailboxProviders';
import { readM24Error } from '../../utils/parseM24LimitError';
import type { MailboxErrorReason } from '../outreachTypes';

/** The tiles of frame o1, in its order. The other providers show on the tile of their family. */
export const TILE_IDS: readonly MailboxProviderId[] = [
  'gmail',
  'outlook',
  'microsoft365',
  'icloud',
  'yahoo',
  'zoho',
  'fastmail',
  'other',
];

/** The tile that a provider lights up: AOL is on the Yahoo tile, the web hosts on "Something else". */
export function tileOf(id: MailboxProviderId | null): MailboxProviderId | null {
  if (id === null) {
    return null;
  }
  if (id === 'aol') {
    return 'yahoo';
  }
  if (id === 'godaddy' || id === 'ionos' || id === 'hostinger') {
    return 'other';
  }
  return TILE_IDS.includes(id) ? id : null;
}

export function providerOf(id: MailboxProviderId): MailboxProvider {
  return MAILBOX_PROVIDERS.find((provider) => provider.id === id) ?? mailboxProviderOf('other');
}

/** The letters of an app password that some providers check (Google, Apple, Yahoo and AOL show 16). */
const APP_PASSWORD_LENGTH: Partial<Record<MailboxProviderId, number>> = {
  gmail: 16,
  icloud: 16,
  yahoo: 16,
  aol: 16,
};

/** The app password with no spaces (Google shows it in groups of 4: "abcd efgh ijkl mnop"). */
export function cleanAppPassword(value: string): string {
  return value.replace(/\s+/g, '');
}

/** The length the guide checks before Connect, or null when the provider has no fixed length. */
export function appPasswordLength(id: MailboxProviderId): number | null {
  return APP_PASSWORD_LENGTH[id] ?? null;
}

/** The letters of a pasted app password (the dashes of Apple do not count). */
export function appPasswordLetters(value: string): number {
  return cleanAppPassword(value).replace(/-/g, '').length;
}

export function isAppPasswordComplete(id: MailboxProviderId, value: string): boolean {
  const length = appPasswordLength(id);
  const letters = appPasswordLetters(value);
  return length === null ? letters > 0 : letters === length;
}

export interface GuideServers {
  imapHost: string;
  imapPort: number | null;
  smtpHost: string;
  smtpPort: number | null;
}

/** The servers of a provider for an address; the detected hosts win (Zoho EU, a web host by MX). */
export function serversOf(
  id: MailboxProviderId,
  email: string,
  detected: { providerId: MailboxProviderId; imap: HostSpec | null; smtp: HostSpec | null } | null
): GuideServers | null {
  const hosts =
    detected !== null && detected.providerId === id
      ? { imap: detected.imap, smtp: detected.smtp }
      : mailboxHostsOf(id, emailDomainOf(email));
  if (hosts.imap === null || hosts.smtp === null) {
    return null;
  }
  return { imapHost: hosts.imap.host, imapPort: hosts.imap.port, smtpHost: hosts.smtp.host, smtpPort: hosts.smtp.port };
}

/** True for a Gmail address on an own domain (Google Workspace). */
export function isGoogleWorkspace(id: MailboxProviderId | null, email: string): boolean {
  const domain = emailDomainOf(email);
  return id === 'gmail' && domain !== null && domain !== 'gmail.com' && domain !== 'googlemail.com';
}

/**
 * Why a check or a connect failed. These come from the connect only: `providerUnsupported`, `taken`
 * (another member connected the address), `mailboxLimit` (the plan has no room for one more
 * mailbox), `supportPaused` (Music24 support paused the address) and `demoReadOnly`.
 */
export type GuideErrorReason =
  | MailboxErrorReason
  | 'providerUnsupported'
  | 'taken'
  | 'mailboxLimit'
  | 'supportPaused'
  | 'demoReadOnly';

export interface GuideError {
  reason: GuideErrorReason;
  /** The server text with the password and the login hidden; shown only on request. */
  detail: string;
}

const REASONS: readonly GuideErrorReason[] = [
  'authFailed',
  'normalPasswordUsed',
  'hostUnreachable',
  'tlsFailed',
  'smtpAuthDisabled',
  'imapDisabled',
  'unknown',
  'providerUnsupported',
  'supportPaused',
];

/** The FORBIDDEN refusals of a connect, by their `M24_*` code (final review I7). */
const REASON_BY_CODE: Record<string, GuideErrorReason> = {
  M24_TEAM_OWNER_ONLY: 'taken',
  M24_LIMIT_FEATURE: 'mailboxLimit',
  M24_DEMO_READ_ONLY: 'demoReadOnly',
};

/** The plain reason of a refused connect (`error.data.reason` of music24-api). */
export function guideErrorOf(error: unknown): GuideError {
  const data = (error as { data?: Record<string, unknown> } | null)?.data ?? {};
  const reason = REASONS.find((item) => item === data.reason);
  const detail = typeof data.detail === 'string' ? data.detail : '';
  if (reason !== undefined) {
    return { reason, detail };
  }
  const code = readM24Error(error)?.code;
  return { reason: (code === undefined ? undefined : REASON_BY_CODE[code]) ?? 'unknown', detail };
}
